import React, { useState } from "react";
import {
  Box,
  Card,
  Dialog,
  IconButton,
  useTheme,
  alpha,
  Skeleton,
  Typography,
  Chip,
} from "@mui/material";
import {
  Close,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Collections,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

interface MallGalleryProps {
  images: string[];
  mallName: string;
}

/**
 * MallGallery Component
 *
 * Premium image gallery with lightbox viewer:
 * - Responsive grid layout (desktop) and horizontal scroll (mobile)
 * - Full-screen lightbox with navigation
 * - Image counter and close controls
 * - Smooth animations and transitions
 * - Lazy loading for performance
 *
 * Features glass-morphism design and gradient accents.
 */
const MallGallery: React.FC<MallGalleryProps> = ({ images, mallName }) => {
  const theme = useTheme();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [showGalleryGrid, setShowGalleryGrid] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState<
    Record<number, boolean>
  >({});

  const handleImageClick = (index: number) => {
    // Direct click opens the specific image in lightbox
    setShowGalleryGrid(true);
    setSelectedImageIndex(index);
  };

  const handleViewAllClick = () => {
    // View All button opens gallery grid view
    setShowGalleryGrid(true);
    setSelectedImageIndex(null);
  };

  const handleClose = () => {
    setShowGalleryGrid(false);
    setSelectedImageIndex(null);
  };

  const handleBackToGrid = () => {
    setSelectedImageIndex(null);
  };

  const handlePrevious = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedImageIndex !== null && selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const handleImageLoad = (index: number) => {
    setImageLoadStates((prev) => ({ ...prev, [index]: true }));
  };

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return;

      switch (e.key) {
        case "ArrowLeft":
          if (selectedImageIndex > 0) {
            setSelectedImageIndex(selectedImageIndex - 1);
          }
          break;
        case "ArrowRight":
          if (selectedImageIndex < images.length - 1) {
            setSelectedImageIndex(selectedImageIndex + 1);
          }
          break;
        case "Escape":
          setSelectedImageIndex(null);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImageIndex, images.length]);

  if (!images || images.length === 0) {
    return (
      <Card
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.95
          )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          backdropFilter: "blur(10px)",
          p: 4,
          textAlign: "center",
        }}
      >
        <Collections sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          No images available
        </Typography>
      </Card>
    );
  }

  return (
    <>
      {/* Gallery Grid - Show first 6 images as preview */}
      <Box>
        {/* Desktop & Tablet: Grid Layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: { xs: 1.5, sm: 2, md: 2.5 },
          }}
        >
          {images.slice(0, 6).map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <GalleryImageCard
                image={image}
                index={index}
                isLoaded={imageLoadStates[index]}
                onLoad={handleImageLoad}
                onClick={handleImageClick}
              />
            </motion.div>
          ))}
        </Box>

        {/* View All Button - Show when there are more than 6 images */}
        {images.length > 6 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Box
                onClick={handleViewAllClick}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  border: `2px solid ${theme.palette.primary.main}`,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: theme.palette.primary.main,
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 20px ${alpha(
                      theme.palette.primary.main,
                      0.4
                    )}`,
                    "& .MuiTypography-root": {
                      color: "white",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                <Collections
                  sx={{
                    color: "primary.main",
                    fontSize: { xs: 20, sm: 22 },
                    transition: "all 0.3s ease",
                  }}
                />
                <Typography
                  sx={{
                    color: "primary.main",
                    fontWeight: 700,
                    fontSize: { xs: "0.875rem", sm: "0.938rem" },
                    transition: "all 0.3s ease",
                  }}
                >
                  View All {images.length} Photos
                </Typography>
                <ZoomIn
                  sx={{
                    color: "primary.main",
                    fontSize: { xs: 18, sm: 20 },
                    transition: "all 0.3s ease",
                  }}
                />
              </Box>
            </motion.div>
          </Box>
        )}
      </Box>

      {/* Lightbox Dialog - Full Gallery View */}
      <Dialog
        open={showGalleryGrid}
        onClose={handleClose}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor:
              selectedImageIndex !== null
                ? "rgba(0, 0, 0, 0.95)"
                : "rgba(18, 18, 18, 0.98)",
            backdropFilter: "blur(20px)",
          },
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {selectedImageIndex !== null ? (
            // Individual Image View
            <motion.div
              key={`image-${selectedImageIndex}`}
              initial={false}
              animate={{ opacity: 1 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.1}
              onDragEnd={(e, info) => {
                const threshold = 50;
                if (info.offset.x > threshold && selectedImageIndex > 0) {
                  handlePrevious();
                } else if (
                  info.offset.x < -threshold &&
                  selectedImageIndex < images.length - 1
                ) {
                  handleNext();
                }
              }}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                cursor: "grab",
              }}
            >
              {/* Back to Grid Button */}
              <IconButton
                onClick={handleBackToGrid}
                sx={{
                  position: "absolute",
                  top: { xs: 16, md: 24 },
                  left: { xs: 16, md: 24 },
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  zIndex: 2,
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <Collections />
              </IconButton>

              {/* Close Button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: { xs: 16, md: 24 },
                  right: { xs: 16, md: 24 },
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  zIndex: 2,
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <Close />
              </IconButton>

              {/* Image Counter */}
              <Chip
                label={`${selectedImageIndex + 1} / ${images.length}`}
                sx={{
                  position: "absolute",
                  top: { xs: 16, md: 24 },
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  fontWeight: 600,
                  zIndex: 2,
                }}
              />

              {/* Previous Button */}
              {selectedImageIndex > 0 && (
                <IconButton
                  onClick={handlePrevious}
                  sx={{
                    position: "absolute",
                    left: { xs: 8, md: 24 },
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    color: "white",
                    zIndex: 2,
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                    },
                  }}
                >
                  <ChevronLeft sx={{ fontSize: 32 }} />
                </IconButton>
              )}

              {/* Next Button */}
              {selectedImageIndex < images.length - 1 && (
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    right: { xs: 8, md: 24 },
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    color: "white",
                    zIndex: 2,
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                    },
                  }}
                >
                  <ChevronRight sx={{ fontSize: 32 }} />
                </IconButton>
              )}

              {/* Full-Screen Image */}
              <Box
                component="img"
                src={images[selectedImageIndex]}
                alt={`${mallName} - Image ${selectedImageIndex + 1}`}
                sx={{
                  maxWidth: "90%",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  borderRadius: 2,
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />
            </motion.div>
          ) : (
            // Gallery Grid View
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                width: "100%",
                height: "100%",
                overflow: "auto",
                position: "relative",
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "fixed",
                  top: { xs: 16, md: 24 },
                  right: { xs: 16, md: 24 },
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  zIndex: 1300,
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <Close />
              </IconButton>

              {/* Gallery Header */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1200,
                  bgcolor: "rgba(18, 18, 18, 0.95)",
                  backdropFilter: "blur(20px)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  py: { xs: 2, md: 3 },
                  px: { xs: 2, md: 4 },
                }}
              >
                <Box sx={{ maxWidth: 1400, mx: "auto" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      pr: { xs: 6, md: 8 },
                    }}
                  >
                    <Collections sx={{ color: "white", fontSize: 28 }} />
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: "white",
                          fontWeight: 700,
                          fontSize: { xs: "1.25rem", md: "1.5rem" },
                        }}
                      >
                        {mallName} Gallery
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255, 255, 255, 0.7)", mt: 0.5 }}
                      >
                        {images.length}{" "}
                        {images.length === 1 ? "photo" : "photos"} • Click any
                        image to view
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Gallery Grid */}
              <Box
                sx={{
                  maxWidth: 1400,
                  mx: "auto",
                  p: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                      lg: "repeat(5, 1fr)",
                    },
                    gap: { xs: 1.5, sm: 2, md: 2.5 },
                  }}
                >
                  {images.map((image, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.02 }}
                    >
                      <Box
                        onClick={() => setSelectedImageIndex(index)}
                        sx={{
                          position: "relative",
                          paddingBottom: "100%",
                          borderRadius: 2,
                          overflow: "hidden",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          border: "2px solid transparent",
                          "&:hover": {
                            transform: "scale(1.05)",
                            border: `2px solid ${theme.palette.primary.main}`,
                            zIndex: 10,
                            boxShadow: `0 8px 24px ${alpha(
                              theme.palette.primary.main,
                              0.5
                            )}`,
                            "& .hover-overlay": {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={image}
                          alt={`${mallName} - Photo ${index + 1}`}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          loading="lazy"
                        />
                        <Box
                          className="hover-overlay"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background: alpha(theme.palette.common.black, 0.4),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.3s ease",
                          }}
                        >
                          <ZoomIn sx={{ color: "white", fontSize: 32 }} />
                        </Box>
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog>
    </>
  );
};

// Gallery Image Card Component
interface GalleryImageCardProps {
  image: string;
  index: number;
  isLoaded?: boolean;
  onLoad: (index: number) => void;
  onClick: (index: number) => void;
}

const GalleryImageCard: React.FC<GalleryImageCardProps> = ({
  image,
  index,
  isLoaded,
  onLoad,
  onClick,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        aspectRatio: "16/10",
        bgcolor: "grey.100",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "scale(1.05) translateY(-8px)",
          boxShadow: 8,
          "& .overlay": {
            opacity: 1,
          },
          "& .zoom-icon": {
            opacity: 1,
            transform: "translate(-50%, -50%) scale(1)",
          },
        },
      }}
      onClick={() => onClick(index)}
    >
      {/* Loading Skeleton */}
      {!isLoaded && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{ position: "absolute", top: 0, left: 0 }}
        />
      )}

      {/* Image */}
      <Box
        component="img"
        src={image}
        alt={`Gallery image ${index + 1}`}
        onLoad={() => onLoad(index)}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: isLoaded ? "block" : "none",
        }}
      />

      {/* Hover Overlay */}
      <Box
        className="overlay"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.8
          )}, ${alpha(theme.palette.secondary.main, 0.8)})`,
          opacity: 0,
          transition: "opacity 0.3s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />

      {/* Zoom Icon */}
      <Box
        className="zoom-icon"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%) scale(0.8)",
          opacity: 0,
          transition: "all 0.3s ease",
          bgcolor: "white",
          borderRadius: "50%",
          width: 56,
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
          boxShadow: 4,
        }}
      >
        <ZoomIn sx={{ fontSize: 32 }} />
      </Box>

      {/* Image Number Badge */}
      <Chip
        label={`#${index + 1}`}
        size="small"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          bgcolor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(10px)",
          color: "white",
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      />
    </Card>
  );
};

export default MallGallery;
